import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { displayArchivoNombre } from "../utils/archivoDisplayName";
import { getCourseFileObjectKey, getCourseFileViewUrl } from "../utils/signedCourseFileUrl";
import {
  isAudioCourseMaterial,
  isPdfCourseMaterial,
  isSpreadsheetCourseMaterial,
  isVideoCourseMaterial,
} from "../utils/courseMaterialFileKind";
import { getMessage } from "../data/locales";
import { API_URL } from "../config";
import CoursePdfDocumentViewer from "./CoursePdfDocumentViewer";
import CourseSpreadsheetPreview from "./CourseSpreadsheetPreview";
import "./CourseMaterialEmbedModal.css";

const ALWAYS_ALLOWED_DEMO_VIDEO_HOSTS = new Set(["interactive-examples.mdn.mozilla.net"]);
// archive.org demo videos are only useful for local testing; disallow them elsewhere.
const LOCAL_ONLY_DEMO_VIDEO_HOSTS = new Set(["archive.org"]);
const IS_LOCAL_ENVIRONMENT = API_URL.toLowerCase().includes("local");

const VIDEO_SEEK_SECONDS = 30;
const VIDEO_SEEK_CONTROLS_HIDE_DELAY_MS = 2500;

function isAllowedDemoVideoUrl(value) {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:") {
      return false;
    }
    if (ALWAYS_ALLOWED_DEMO_VIDEO_HOSTS.has(url.hostname)) {
      return true;
    }
    return IS_LOCAL_ENVIRONMENT && LOCAL_ONLY_DEMO_VIDEO_HOSTS.has(url.hostname);
  } catch {
    return false;
  }
}

function resolveCourseMaterialViewUrl(archivo) {
  const objectKey = getCourseFileObjectKey(archivo);
  try {
    if (objectKey) {
      return getCourseFileViewUrl(objectKey);
    }
    const directUrl = typeof archivo?.url === "string" ? archivo.url.trim() : "";
    return isAllowedDemoVideoUrl(directUrl) ? directUrl : "";
  } catch {
    return "";
  }
}

function CourseMaterialInlineViewer({ archivo, canDownload = false }) {
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [areSeekControlsVisible, setAreSeekControlsVisible] = useState(true);
  const hideSeekControlsTimeoutRef = useRef(null);
  const viewUrl = archivo ? resolveCourseMaterialViewUrl(archivo) : "";
  const title = archivo
    ? displayArchivoNombre(archivo, "") || getMessage("campus.materialViewerTitle")
    : getMessage("campus.materialViewerTitle");

  useEffect(() => {
    const clearHideSeekControlsTimeout = () => {
      if (hideSeekControlsTimeoutRef.current) {
        window.clearTimeout(hideSeekControlsTimeoutRef.current);
        hideSeekControlsTimeoutRef.current = null;
      }
    };

    const scheduleHideSeekControls = () => {
      clearHideSeekControlsTimeout();
      hideSeekControlsTimeoutRef.current = window.setTimeout(() => {
        setAreSeekControlsVisible(false);
      }, VIDEO_SEEK_CONTROLS_HIDE_DELAY_MS);
    };

    const updateFullscreenState = () => {
      setIsFullscreen(document.fullscreenElement === playerRef.current);
      setAreSeekControlsVisible(true);
      scheduleHideSeekControls();
    };
    document.addEventListener("fullscreenchange", updateFullscreenState);
    return () => {
      document.removeEventListener("fullscreenchange", updateFullscreenState);
      clearHideSeekControlsTimeout();
    };
  }, []);

  useEffect(() => {
    const clearHideSeekControlsTimeout = () => {
      if (hideSeekControlsTimeoutRef.current) {
        window.clearTimeout(hideSeekControlsTimeoutRef.current);
        hideSeekControlsTimeoutRef.current = null;
      }
    };

    const scheduleHideSeekControls = () => {
      clearHideSeekControlsTimeout();
      hideSeekControlsTimeoutRef.current = window.setTimeout(() => {
        setAreSeekControlsVisible(false);
      }, VIDEO_SEEK_CONTROLS_HIDE_DELAY_MS);
    };

    const player = playerRef.current;
    if (!player) {
      return clearHideSeekControlsTimeout;
    }

    const handleMouseMove = () => {
      setAreSeekControlsVisible(true);
      scheduleHideSeekControls();
    };

    setAreSeekControlsVisible(true);
    scheduleHideSeekControls();
    player.addEventListener("mousemove", handleMouseMove);

    return () => {
      player.removeEventListener("mousemove", handleMouseMove);
      clearHideSeekControlsTimeout();
    };
  }, [archivo, viewUrl]);

  const seekVideo = useCallback((seconds) => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const targetTime = Math.max(0, video.currentTime + seconds);
    video.currentTime = Number.isFinite(video.duration)
      ? Math.min(targetTime, video.duration)
      : targetTime;
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      setAreSeekControlsVisible(true);
      if (document.fullscreenElement === playerRef.current) {
        await document.exitFullscreen();
        return;
      }
      await playerRef.current?.requestFullscreen();
    } catch (error) {
      console.error("Unable to toggle video fullscreen mode:", error);
    }
  }, []);

  const handleVideoDoubleClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
    },
    [],
  );

  if (!archivo) {
    return null;
  }

  if (!viewUrl) {
    return <p className="course-material-embed-error">{getMessage("cursoUsuario.fileOpenFailed")}</p>;
  }

  if (isPdfCourseMaterial(archivo)) {
    return <CoursePdfDocumentViewer viewUrl={viewUrl} title={title} canDownload={canDownload} />;
  }

  if (isVideoCourseMaterial(archivo)) {
    return (
      <div
        ref={playerRef}
        className={`course-material-video-player${
          areSeekControlsVisible ? " is-seek-controls-visible" : ""
        }`}
      >
        <video
          ref={videoRef}
          className="course-material-embed-video"
          src={viewUrl}
          controls
          controlsList="nodownload noremoteplayback nofullscreen"
          disablePictureInPicture
          playsInline
          preload="metadata"
          aria-label={title}
          onDoubleClickCapture={handleVideoDoubleClick}
          onDoubleClick={handleVideoDoubleClick}
        >
          <track kind="captions" label="Subtítulos no disponibles" />
        </video>
        <div className="course-material-video-seek-controls" aria-label="Controles de avance de vídeo">
          <button
            type="button"
            className="course-material-video-seek-button"
            onClick={() => seekVideo(-VIDEO_SEEK_SECONDS)}
            aria-label="Retroceder 30 segundos"
          >
            -30 s
          </button>
          <button
            type="button"
            className="course-material-video-seek-button"
            onClick={() => seekVideo(VIDEO_SEEK_SECONDS)}
            aria-label="Avanzar 30 segundos"
          >
            +30 s
          </button>
          <button
            type="button"
            className="course-material-video-seek-button"
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? "Salir de pantalla completa" : "Ver en pantalla completa"}
          >
            {isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
          </button>
        </div>
      </div>
    );
  }

  if (isAudioCourseMaterial(archivo)) {
    return (
      <audio
        className="course-material-embed-audio"
        src={viewUrl}
        controls
        controlsList="nodownload"
      >
        <track kind="captions" label="Transcripción no disponible" />
      </audio>
    );
  }

  if (isSpreadsheetCourseMaterial(archivo)) {
    return <CourseSpreadsheetPreview viewUrl={viewUrl} />;
  }

  return (
    <iframe
      title={title}
      src={viewUrl}
      className="course-material-embed-frame"
      scrolling="yes"
      sandbox="allow-same-origin allow-scripts"
    />
  );
}

CourseMaterialInlineViewer.propTypes = {
  archivo: PropTypes.shape({
    nombre: PropTypes.string,
    storageKey: PropTypes.string,
    url: PropTypes.string,
  }),
  canDownload: PropTypes.bool,
};

CourseMaterialInlineViewer.defaultProps = {
  archivo: null,
  canDownload: false,
};

export default CourseMaterialInlineViewer;
